import type { CSSProperties } from 'react';
import { css } from 'styled-components';

import type { CustomizableSectionProps } from 'hb-builder/components/BuilderComponents/common/Section/components/CustomizableSection';
import {
  BACKGROUND_ARRANGEMENT,
  BACKGROUND_POSITION,
  BACKGROUND_REPEAT,
} from 'hb-builder/constants/imageArrangement';
import { getAlignItems } from 'hb-builder/utils/attributes';
import { getNetworkColor } from 'hb-builder/utils/colors';
import { paddingOverride } from 'hb-builder/utils/mobile';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

export const getWrapperSectionStyle = ({
  attributes,
  width = '100%',
  colors,
  ...otherProps
}) => ({
  ...(attributes?.fullWidth && {
    backgroundColor: getNetworkColor(
      attributes.containerBackgroundColor,
      colors,
    ),
    ...((attributes?.containerBackgroundImage || attributes?._imageData) && {
      backgroundSize:
        attributes.containerBackgroundSize || BACKGROUND_ARRANGEMENT.COVER,
      backgroundPosition:
        attributes.containerBackgroundPosition || BACKGROUND_POSITION.CENTER,
      backgroundRepeat:
        attributes.containerBackgroundRepeat || BACKGROUND_REPEAT.NO_REPEAT,
      ...(attributes.backgroundType !== 'video' && {
        backgroundImage: `url(${
          attributes._imageData || attributes.containerBackgroundImage
        })`,
      }),
    }),

    ...(attributes?.containerBoxShadow && {
      boxShadow: attributes.containerBoxShadow,
    }),
  }),
  maxWidth: '100%',
  minHeight: attributes.height ?? 'inherit',
  height: 'auto',
  width,
  ...otherProps,
});

export const getWrapperInnerSectionBg = ({ attributes, colors }) => ({
  ...(!attributes?.fullWidth && {
    backgroundColor: getNetworkColor(
      attributes.containerBackgroundColor,
      colors,
    ),
    borderRadius: attributes.cornerRadius,

    ...((attributes?.containerBackgroundImage || attributes?._imageData) && {
      backgroundSize:
        attributes.containerBackgroundSize || BACKGROUND_ARRANGEMENT.COVER,
      backgroundPosition: attributes.containerBackgroundPosition,
      backgroundRepeat: attributes.containerBackgroundRepeat,
      ...(attributes.__loading && { filter: 'brightness(0.4)' }),
      ...(attributes.backgroundType !== 'video' && {
        backgroundImage: `url(${
          attributes._imageData || attributes.containerBackgroundImage
        })`,
      }),
    }),
  }),
  width: '100%',
});

export interface WrapperInnerSectionStyleProps {
  attributes: SectionItemAttributes;
  componentType: string;
  isMobile: CustomizableSectionProps['isMobile'];
  isSmallScreen: boolean;
}

export const getWrapperInnerSectionStyle = ({
  attributes,
  componentType,
  isMobile,
  isSmallScreen,
}: WrapperInnerSectionStyleProps) => {
  const flexDirectionProperty =
    attributes?.flexDirection === 'column' ? 'justifyContent' : 'alignItems';
  const mobileFlexDirection =
    !attributes?.mobileDisplay || attributes?.mobileDisplay === 'stack'
      ? 'column'
      : 'row';

  return {
    height: '100%',
    width: '100%',
    maxWidth:
      isMobile || attributes.outsideGrid || isSmallScreen
        ? '100%'
        : attributes?.sectionWidth,

    // flexDirection is set with `mobileDisplay` attributes on mobile.
    flexDirection: isMobile ? mobileFlexDirection : attributes?.flexDirection,

    ...(isMobile
      ? {
          padding: paddingOverride({
            keepContainerPaddingScreenS:
              attributes?.keepContainerPaddingScreenS,
            padding: attributes?.containerPadding?.toString(),
            type: componentType,
          }),
        }
      : {
          padding: attributes?.containerPadding,
        }),

    // alignment property name changes with flex direction value
    [flexDirectionProperty]: getAlignItems(attributes?.verticalAlign),
  } as CSSProperties;
};

export interface MobileDisplayProps {
  $mobileDisplay?: SectionItemAttributes['mobileDisplay'];
  $verticalAlign?: SectionItemAttributes['verticalAlign'];
}

const getMobileStyle = ({
  $mobileDisplay,
  $verticalAlign,
}: MobileDisplayProps) =>
  css`
    // When on mobile and with the stack option, we override width and height column rules.
    ${(!$mobileDisplay || $mobileDisplay === 'stack') &&
    `
    width: 100% !important;
    flex-direction: column !important;
    justify-content: ${getAlignItems($verticalAlign)};

    .builder-column {
      width: 100% !important;
    }

    .builder-column-empty {
      height: 200px !important;
    }
  `}
  `;

export interface WrapperInnerStyleProps extends MobileDisplayProps {
  $isMobile: SectionItemAttributes['isMobile'];
  $minHeight: SectionItemAttributes['minHeight'];
}

export const wrapperInnerStyle = ({
  $isMobile,
  $minHeight,
}: WrapperInnerStyleProps) =>
  css`
    margin: auto;
    flex-direction: row;
    min-height: ${$minHeight ? `${$minHeight}px` : 'inherit'};

    ${$isMobile && getMobileStyle}
  `;

export const getInnerStyle = ({
  attributes,
  colors,
  componentType,
  isMobile,
  isSmallScreen,
}) => ({
  ...getWrapperInnerSectionStyle({
    attributes,
    componentType,
    isMobile,
    isSmallScreen,
  }),
  ...getWrapperInnerSectionBg({
    attributes,
    colors,
  }),
});

export const getInnerStyleWithDynamicComponent = ({ bodyWidth }) =>
  bodyWidth !== 'auto' ? { justifyContent: 'center' } : {};
